import React from 'react';
import { ShoppingCartIcon, XIcon, ArrowRightIcon } from '@/components/icons';
import { formatTime, formatCurrencyForDisplay } from '@/lib/utils';
import type { TableAvailabilitySlot } from '@/features/booking/types/tableBookingTypes';


interface BookingSummaryProps {
  selectedBookings: Record<string, TableAvailabilitySlot>;
  totalPrice: number;
  onRemoveBooking: (tableNumber: string) => void;
  onBooking: () => void;
  businessTimeZone?: string;
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  selectedBookings,
  totalPrice,
  onRemoveBooking,
  onBooking,
  businessTimeZone,
  currency
}) => {
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 space-y-4">
      <h2 className="flex items-center text-xl font-semibold">
        <ShoppingCartIcon />
        <span className="ml-3">Your Booking Summary</span>
      </h2>

      {Object.keys(selectedBookings).length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>Your selected tables will appear here.</p>
          <p className="text-sm">Please choose a date and select a table from the map.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(selectedBookings).map(([tableNum, slot]) => (
            <div key={tableNum} className="flex items-center justify-between bg-gray-800/60 p-3 rounded-lg">
              <div>
                <span className="font-bold">Table {tableNum}</span>
                <div className="text-sm text-gray-400">
                  {formatTime(slot.start_time, businessTimeZone)} - {slot.event_booking_category.booking_category.name}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-semibold">{formatPrice(slot.event_booking_category.price)}</span>
                <button 
                  onClick={() => onRemoveBooking(tableNum)} 
                  className="p-1 text-gray-500 hover:text-red-400 hover:bg-gray-700 rounded-full transition-colors"
                >
                  <XIcon/>
                </button>
              </div>
            </div>
          ))}

          <div className="pt-4 border-t border-gray-700 space-y-4">
            <div className="flex justify-between items-center text-lg">
              <span className="text-gray-300">Total Price</span>
              <span className="font-bold text-2xl">{formatPrice(totalPrice)}</span>
            </div>
            <button 
              onClick={onBooking}
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:scale-100"
              disabled={totalPrice === 0}
            >
              Book {Object.keys(selectedBookings).length} Table(s) <ArrowRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSummary;
